import React, { useState } from "react";
import { Button, Col, Collapse, Row, Space } from "antd";
import { SvgIcon } from "../../../../common/components/SvgIcon";
import { HtmlSummaryEditModal } from "./HtmlSummaryEditModal";
import { SummarySection } from "../../../../types/ContentTypes";
import { FeedbackCreation, ReportFeedback } from "../../../../types/ReportTypes";
import { GenericTabs } from "../../../../common/components/GenericTabs";
import { Sanitized } from "../../../../common/components/Sanitized";
import { Chapter } from "./Outline";
import { SectionFeedback } from "./SectionFeedback";
import styles from "./MultiSectionHtmlSummary.module.less";

const { Panel } = Collapse;

interface MultiSectionHtmlSummaryProps {
    title: string;
    description: string;
    html: string;
    onUpdate?: (summary: string) => void;
    editable?: boolean;
    sections?: SummarySection[];
    type?: "default" | "tab";
    selectedSection?: string;
    onSectionChange?: (chapter: Chapter, section: string) => void;
    panelKey: Chapter;
    activeKeys?: string[];
    onActiveKeyChange?: (activeKeys: Chapter[]) => void;
    feedback?: ReportFeedback[];
    onFeedbackAdd?: (feedback: FeedbackCreation) => Promise<void>;
    onFeedbackRemove?: (feedbackId?: number) => Promise<void>;
}

export const MultiSectionHtmlSummary: React.FC<MultiSectionHtmlSummaryProps> = ({
    title,
    description,
    html,
    onUpdate,
    editable = false,
    sections,
    type = "default",
    selectedSection,
    onSectionChange,
    panelKey,
    activeKeys,
    onActiveKeyChange,
    feedback,
    onFeedbackAdd,
    onFeedbackRemove,
}) => {
    const [showEdit, setShowEdit] = useState(false);

    const editButton = editable ? (
        <Col>
            <Space>
                <Button
                    className={styles.edit}
                    type="link"
                    onClick={(e) => {
                        setShowEdit(true);
                        e.stopPropagation();
                    }}
                >
                    Edit
                </Button>
            </Space>
        </Col>
    ) : null;

    const content =
        type === "tab" && sections && sections.length > 1 ? (
            <GenericTabs
                activeKey={selectedSection}
                onChange={(key) => {
                    onSectionChange?.(panelKey, key);
                }}
                items={sections.map((s, index) => ({
                    label: <Sanitized className={styles.tabTitle} content={s.key} />,
                    key: index.toString(),
                    children: (
                        <div className={styles.section}>
                            <Sanitized className="il-summary" content={s.value} />
                            <SectionFeedback
                                className={styles.feedback}
                                feedback={feedback}
                                sectionIdentifier={`${panelKey}.${index + 1}`}
                                sectionName={s.key}
                                onFeedbackAdd={onFeedbackAdd}
                                onFeedbackRemove={onFeedbackRemove}
                            />
                        </div>
                    ),
                }))}
            />
        ) : (
            <>
                {sections?.map((s, index) => (
                    <div className={styles.section} key={index}>
                        <Sanitized className="il-summary" content={s.value} />
                        <SectionFeedback
                            className={styles.feedback}
                            feedback={feedback}
                            sectionIdentifier={`${panelKey}.${index + 1}`}
                            sectionName={s.key}
                            onFeedbackAdd={onFeedbackAdd}
                            onFeedbackRemove={onFeedbackRemove}
                        />
                    </div>
                ))}
            </>
        );

    return (
        <>
            <HtmlSummaryEditModal
                open={showEdit}
                title={title}
                summary={html}
                onOk={(value) => {
                    onUpdate?.(value);
                    setShowEdit(false);
                }}
                onCancel={() => setShowEdit(false)}
                width={1200}
            />
            <Collapse
                className={styles.summary}
                bordered={false}
                collapsible="header"
                expandIconPosition="end"
                expandIcon={() => <SvgIcon name="down" size={20} className={styles.expandIcon} />}
                activeKey={activeKeys}
                onChange={(value) => {
                    onActiveKeyChange?.(value as Chapter[]);
                }}
            >
                <Panel
                    header={
                        <Row align="middle">
                            <Col flex={1}>
                                <div className={styles.headerTitle}>{title}</div>
                                <div>{description}</div>
                            </Col>
                            {editButton}
                        </Row>
                    }
                    key={panelKey}
                >
                    {content}
                </Panel>
            </Collapse>
        </>
    );
};
